// MF-67 — Journal insights: headline numbers + trend + type split + top reasons for a set of
// entry filters. Self-contained (fetches its own stats) so it can be dropped anywhere: the Entries
// page today, a student profile (pass { studentId }) or a school dashboard later.
// Props: filters = the same query the entries list uses (categoryCode, type, status, from, to,
// search, studentId…); `top` = how many templates to rank.
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, CircularProgress, Typography } from '@mui/material'
import ChartCard from './ChartCard'
import KpiTiles from './KpiTiles'
import TrendChart from './TrendChart'
import TypeDonut from './TypeDonut'
import TopReasonsChart from './TopReasonsChart'
import { orderedTypes, typeStyle } from './chartTheme'
import { useTypes } from '../components/useLookups'
import { getEntriesStats } from '../../../../services/journal/entries.service'

const browserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}

const Legend = ({ codes, types }) => (
  <div className='flex items-center flex-wrap' style={{ gap: 16 }}>
    {codes.map((code) => {
      const st = typeStyle(code, types.byCode)
      return (
        <span
          key={code}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'rgb(100, 116, 139)'
          }}
        >
          <span style={{ width: 12, height: 4, borderRadius: 2, background: st.color }} />
          {st.label}
        </span>
      )
    })}
  </div>
)

const JournalInsights = ({ filters = {}, top = 10 }) => {
  const tenantTz = useSelector((state) => state?.tenant?.tenant?.timezone)
  const types = useTypes()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const key = JSON.stringify(filters)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError('')
    const params = { ...JSON.parse(key), tz: tenantTz || browserTz(), top }
    Object.keys(params).forEach((k) => (params[k] === '' || params[k] == null) && delete params[k])
    getEntriesStats(params)
      .then(({ data }) => alive && setStats(data))
      .catch(() => alive && setError('Could not load the insights. Try again in a moment.'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [key, tenantTz, top])

  if (loading && !stats) {
    return (
      <div className='flex justify-center' style={{ padding: '48px 0' }}>
        <CircularProgress />
      </div>
    )
  }
  if (error) {
    return <Typography style={{ padding: 24, color: 'rgb(185, 28, 28)', fontSize: 13 }}>{error}</Typography>
  }
  if (!stats?.totals?.entries) {
    return (
      <div
        className='text-center'
        style={{ padding: 40, background: '#fff', border: '1px solid rgb(226, 232, 240)', borderRadius: 12 }}
      >
        <Typography style={{ color: 'rgb(100, 116, 139)', fontSize: 13 }}>
          No entries match these filters.
        </Typography>
      </div>
    )
  }

  const topCodes = orderedTypes([...new Set((stats.topTemplates || []).map((r) => r.type))], types.list)

  return (
    <div
      className='flex flex-col'
      style={{ gap: 16, opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}
    >
      <KpiTiles totals={stats.totals} byType={stats.byType} types={types} />
      <Box
        className='grid'
        style={{ gap: 16 }}
        sx={{ gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 2fr) minmax(0, 1fr)' } }}
      >
        <ChartCard
          title='Trend'
          description={`Entries per ${stats.bucket === 'week' ? 'week' : 'month'}, by type.`}
        >
          <TrendChart trend={stats.trend} bucket={stats.bucket} types={types} />
        </ChartCard>
        <ChartCard title='Balance' description='Share of entries by type.'>
          <TypeDonut byType={stats.byType} types={types} />
        </ChartCard>
      </Box>
      <ChartCard
        title={`Top ${stats.topTemplates.length} reasons`}
        description='The templates used most often.'
      >
        {topCodes.length > 1 ? <Legend codes={topCodes} types={types} /> : null}
        <TopReasonsChart topTemplates={stats.topTemplates} types={types} />
      </ChartCard>
    </div>
  )
}

export default JournalInsights
